import { useEffect, useRef } from 'react';
import {
  useQuery,
  useQueryClient,
  keepPreviousData,
  hashKey,
  type QueryClient,
} from '@tanstack/react-query';
import { api } from '../services/api';

// Backend envelopa em IGetResponseBase: { data, message, meta }.
interface IApiEnvelope<T> {
  data: T;
  message?: string;
  meta?: unknown;
}

export type StatusConf = 'ok' | 'erro' | 'aviso' | 'sem_dados';
export type TipoDoc = 'nfe' | 'nfce' | 'cte' | 'nfse';
export type LadoNota = 'so_fiscal' | 'so_dominio' | 'par' | 'fora_escopo';
// Filtro clicável dos KPIs do drawer (lados + grupos por fonte).
export type LadoFiltro = LadoNota | 'dominio' | 'fiscal';
export type Confianca = 'alta' | 'media' | 'baixa';
// Status da captura do Autmais (selo em cima do subcard Fiscal).
export type AutmaisStatus = 'ok' | 'aviso' | 'erro' | 'processando' | 'sem_log';
export type EmpresaBuscaModo = 'razao' | 'cnpj' | 'id';

interface IRange {
  data_ini?: string;
  data_fim?: string;
}

// ---------- /totais ----------
export interface ICanceladasPorFonte {
  dominio: number;
  fiscal: number;
  portal: number;
}
export interface ISerieMes {
  mes: string;
  notas_dominio: number;
  notas_fiscal: number;
  notas_portal: number;
  devolucao: number; // Domínio saída (própria)
  devolucao_venda: number; // Domínio entrada (venda)
  devolucao_fiscal: number;
  devolucao_fiscal_venda: number;
  devolucao_fiscal_propria: number;
  canceladas_dominio: number;
  canceladas_fiscal: number;
  canceladas_portal: number;
  empresas: number; // Domínio
  empresas_autmais: number;
  empresas_portal: number;
}
export interface IConferenciaKpis {
  notas_dominio: number;
  notas_fiscal: number;
  notas_portal: number;
  empresas_total: number;
  empresas_ativas: number;
  empresas_com_dados: number;
  empresas_sem_dados: number;
  empresas_dominio: number;
  empresas_autmais: number;
  empresas_portal: number;
  devolucao_notas: number; // Domínio saída (própria)
  devolucao_venda: number; // Domínio entrada (venda)
  devolucao_valor: number;
  devolucao_fiscal: number;
  devolucao_fiscal_venda: number;
  devolucao_fiscal_propria: number;
  canceladas: ICanceladasPorFonte;
  serie: ISerieMes[];
}

const getTotais = async (
  params: IRange,
  incluirSerie: boolean,
  signal?: AbortSignal,
): Promise<IConferenciaKpis> => {
  const res = await api.get<IApiEnvelope<IConferenciaKpis>>('/api/v1/conferencia-fiscal/totais', {
    params: { ...params, incluir_serie: incluirSerie },
    signal,
  });
  return res.data.data;
};

export const useConferenciaTotais = (params: IRange, incluirSerie = true) =>
  useQuery({
    queryKey: ['conferencia-fiscal', 'totais', params, incluirSerie] as const,
    queryFn: ({ signal }) => getTotais(params, incluirSerie, signal),
    // Ao voltar ao periodo, consulta novamente: os bots continuam importando.
    staleTime: 0,
    gcTime: CONFERENCIA_CACHE_MS,
    refetchInterval: 5 * 60 * 1000,
  });

const getSerie = async (params: IRange, signal?: AbortSignal): Promise<ISerieMes[]> => {
  const res = await api.get<IApiEnvelope<ISerieMes[]>>('/api/v1/conferencia-fiscal/serie', {
    params,
    signal,
  });
  return res.data.data;
};

export const useConferenciaSerie = (params: IRange, enabled: boolean) =>
  useQuery({
    queryKey: ['conferencia-fiscal', 'serie', params] as const,
    queryFn: ({ signal }) => getSerie(params, signal),
    enabled,
    staleTime: 0,
    gcTime: CONFERENCIA_CACHE_MS,
    refetchInterval: 5 * 60 * 1000,
  });

// Quanto tempo o período que saiu da tela fica guardado. O padrão do React Query
// (5 min) descartava o mês anterior: voltar a ele recomeçava do zero, com 10 s
// ou mais de tela vazia. Guardado, ele reaparece na hora e só é conferido de
// novo com o banco em segundo plano (staleTime continua 0).
export const CONFERENCIA_CACHE_MS = 30 * 60 * 1000;

// ---------- /empresas ----------
export interface IContagemTipos {
  nfe: number | null;
  nfce: number | null;
  cte: number | null;
  nfse: number | null;
}
export interface ITipoFlags {
  dev: number;
  canc: number;
}
export interface IFlagsTipos {
  nfe: ITipoFlags;
  nfce: ITipoFlags;
  cte: ITipoFlags;
  nfse: ITipoFlags;
}
export interface IFonteContagem {
  entrada: IContagemTipos;
  saida: IContagemTipos;
  total: number;
  entrada_flags: IFlagsTipos;
  saida_flags: IFlagsTipos;
  // Fatura (codi_esp 42) — só o Domínio; mostrada na linha NFS-e, fora da comparação.
  fatura_entrada?: number;
  fatura_saida?: number;
}
// Conferência nota-a-nota: por sentido → tipo → quantas só no Domínio / só na outra fonte.
export interface IDiffCelula {
  so_dom: number;
  so_out: number;
  // notas casadas (mesmo número) mas com SITUAÇÃO divergente (ex.: cancelada só num lado).
  sit_diff?: number;
  // Canceladas que dom_n/out_n EXCLUEM da contagem exibida, pela MESMA régua do
  // drawer. Alimentam o badge ✕ do card: sem elas o card descontava a nota e não
  // tinha como dizer por quê (a lista do drawer a exibe, o card não a contava —
  // as duas partes da tela discordando sobre a mesma nota).
  dom_canc?: number;
  out_canc?: number;
}
export type IConferenciaNotas = Record<string, Record<string, IDiffCelula>>;

export interface IEmpresaCard {
  empresa_id: number;
  external_id: number;
  razao_social: string | null;
  cnpj: string | null;
  uf: string | null;
  municipio: string | null;
  status: StatusConf;
  dominio: IFonteContagem;
  fiscal: IFonteContagem;
  portal: IFonteContagem;
  faturamento: number;
  devolucao_notas: number;
  canceladas_notas: number;
  // notas casadas com situação divergente (cancelada só num lado) — o "falso verde"
  situacao_divergente: number;
  // tamanho do buraco, pra priorizar a fila: notas que não fecham e o R$ que
  // separa Domínio e Autmais
  divergencia_notas: number;
  valor_divergente: number;
  // valor (R$) por fonte no período. Portal só tem NFS-e (mesma assimetria da contagem)
  valor_dominio: number;
  valor_fiscal: number;
  valor_portal: number;
  autmais_status: AutmaisStatus | null;
  autmais_ultima: string | null;
  conferencia?: IConferenciaNotas;
}
// Quantas empresas em cada status. Respeita período + busca, mas NÃO o filtro de
// veredito selecionado (senão a trilha se zera ao ser clicada).
export type IContadoresVeredito = Record<StatusConf, number>;
// Status do ROBÔ de captura — dimensão independente do veredito: mede se o
// Autmais rodou, não se as notas batem. 'sem_log' = não há registro no mês, ou
// seja, a empresa sobre a qual a tela não tem o que afirmar.
export type IContadoresCaptura = Record<AutmaisStatus, number>;
export type OrdemEmpresas = 'padrao' | 'valor_divergente' | 'divergencia';
export type SentidoNota = 'entrada' | 'saida';
// Filtros avançados (drawer) — recorte sobre atributos do card. Tudo opcional;
// ausência = sem filtro. doc/uf são multi (arrays); serializam CSV na URL.
export interface IFiltrosAvancados {
  acionaveis?: boolean; // esconde Conferido/Sem dados
  r_min?: number; // faixa de valor_divergente (R$)
  r_max?: number;
  sentido?: SentidoNota; // divergência em entrada/saída
  doc?: TipoDoc[]; // tipos com divergência
  uf?: string[];
  sit_div?: boolean; // só com situação divergente (falso verde)
  portal_cego?: boolean; // Portal trouxe mais que o Autmais (robô com ponto cego)
}
export interface IContadoresSentido {
  entrada_erro: number;
  entrada_ok: number;
  saida_erro: number;
  saida_ok: number;
}
export interface IEmpresasPage {
  items: IEmpresaCard[];
  total: number;
  page: number;
  size: number;
  pages: number;
  empresas_ativas: number;
  empresas_com_dados: number;
  empresas_sem_dados: number;
  contadores: IContadoresVeredito;
  contadores_captura: IContadoresCaptura;
  contadores_sentido?: IContadoresSentido;
  // total de empresas do período ANTES dos filtros avançados (a "cicatriz"
  // mostra "N de M") e UFs presentes no período (pro multi-select do drawer).
  total_periodo: number;
  ufs_disponiveis: string[];
  // false = o nota-a-nota falhou e o veredito veio do fallback por contagem
  // (menos preciso). A trilha some nesse caso — número não confiável não deve
  // aparecer com cara de confiável.
  conferencia_ok: boolean;
  // Quando o confronto foi calculado (ISO, UTC). O backend guarda o resultado
  // e recalcula em segundo plano a cada ~10 min — a tela mostra o horário para
  // ninguém confundir o dado de minutos atrás com o banco ao vivo.
  calculado_em?: string;
}

type IEmpresasParams = IRange & {
  page: number;
  size: number;
  q?: string;
  q_modo?: EmpresaBuscaModo;
  veredito?: StatusConf;
  captura?: AutmaisStatus;
  ordem?: OrdemEmpresas;
  sentido_status?: string;
  duplicadas?: string;
  // avançados (montados a partir de IFiltrosAvancados na página)
  acionaveis?: boolean;
  r_min?: number;
  r_max?: number;
  sentido?: SentidoNota;
  doc?: string; // CSV
  uf?: string; // CSV
  sit_div?: boolean;
  portal_cego?: boolean;
};

const getEmpresas = async (
  params: IEmpresasParams,
  signal?: AbortSignal,
): Promise<IEmpresasPage> => {
  const res = await api.get<IApiEnvelope<IEmpresasPage>>('/api/v1/conferencia-fiscal/empresas', {
    params,
    signal,
  });
  return res.data.data;
};

// Teto de `size` da API (EMPRESAS_SIZE_MAX no backend).
const EMPRESAS_BLOCO_MAX = 1000;

// Cada chamada de /empresas refaz o confronto do período INTEIRO no banco e só
// depois recorta a página: pedir 30 ou 990 empresas custa o mesmo. Por isso a
// tela pede o bloco inteiro (até 1000 empresas) e fatia as páginas aqui — um
// mês de ~700 empresas vira 1 confronto por consulta, em vez de 5.
const blocoDaPagina = (page: number, size: number) => {
  const pagesPerBlock = Math.max(1, Math.floor(EMPRESAS_BLOCO_MAX / size));
  const block = Math.floor((Math.max(1, page) - 1) / pagesPerBlock) + 1;
  return {
    block,
    pagesPerBlock,
    blockSize: pagesPerBlock * size,
    firstPage: (block - 1) * pagesPerBlock + 1,
  };
};

const empresasKey = (params: IEmpresasParams) =>
  ['conferencia-fiscal', 'empresas', params] as const;

// Busca um bloco, devolve a fatia de `wanted` (se pedida) e guarda as demais
// páginas do bloco. Nunca sobrescreve uma consulta ativa.
const fetchBlock = async (
  queryClient: QueryClient,
  base: IEmpresasParams,
  block: number,
  wanted: number | undefined,
  signal: AbortSignal,
) => {
  const { pagesPerBlock, blockSize } = blocoDaPagina(base.page, base.size);
  const firstPage = (block - 1) * pagesPerBlock + 1;
  const data = await getEmpresas({ ...base, page: block, size: blockSize }, signal);
  const pages = Math.ceil((data.total ?? 0) / base.size);
  const slice = (page: number): IEmpresasPage => {
    const offset = (page - firstPage) * base.size;
    return {
      ...data,
      items: data.items.slice(offset, offset + base.size),
      page,
      size: base.size,
      pages,
    };
  };
  if (!signal.aborted) {
    // As páginas guardadas aqui não têm observador: sem isto valeria o prazo
    // padrão de 5 min e só a página visitada sobreviveria à troca de mês.
    queryClient.setQueryDefaults(['conferencia-fiscal', 'empresas'], {
      gcTime: CONFERENCIA_CACHE_MS,
    });
    const lastPage = Math.min(pages, firstPage + pagesPerBlock - 1);
    for (let page = firstPage; page <= lastPage; page += 1) {
      const key = empresasKey({ ...base, page });
      if (page !== wanted && queryClient.getQueryState(key)?.fetchStatus !== 'fetching') {
        queryClient.setQueryData(key, slice(page));
      }
    }
  }
  return wanted === undefined ? undefined : slice(wanted);
};

export const useConferenciaEmpresas = (params: IEmpresasParams) => {
  const queryClient = useQueryClient();
  const query = useQuery({
    queryKey: empresasKey(params),
    // Sem o `signal` do React Query, de propósito: trocar de mês antes da
    // resposta chegar NÃO cancela a consulta. O banco termina o confronto de
    // qualquer jeito (cancelar o HTTP não cancela o SQL), então abortar só
    // jogava fora um resultado já pago — e o mês anterior voltava vazio.
    queryFn: async () =>
      (await fetchBlock(
        queryClient,
        params,
        blocoDaPagina(params.page, params.size).block,
        params.page,
        new AbortController().signal,
      )) as IEmpresasPage,
    staleTime: 0,
    gcTime: CONFERENCIA_CACHE_MS,
    refetchInterval: 5 * 60 * 1000,
    placeholderData: (previousData, previousQuery) => {
      const previousParams = previousQuery?.queryKey[2] as IEmpresasParams | undefined;
      if (!previousParams) return undefined;
      // Somente a paginação pode reaproveitar a lista anterior. Um novo filtro
      // precisa aguardar seu próprio resultado para não rotular cartões antigos.
      return hashKey([{ ...previousParams, page: undefined }]) ===
        hashKey([{ ...params, page: undefined }])
        ? previousData
        : undefined;
    },
  });
  const paramsKey = JSON.stringify(params);
  const pages = query.isPlaceholderData ? 0 : (query.data?.pages ?? 0);
  const panoramaScope = hashKey([
    {
      ...params,
      page: undefined,
      veredito: undefined,
      captura: undefined,
      sentido_status: undefined,
      duplicadas: undefined,
    },
  ]);
  type Panorama = Pick<
    IEmpresasPage,
    'contadores' | 'contadores_captura' | 'contadores_sentido' | 'conferencia_ok'
  >;
  const previousPanorama = useRef<{ scope: string; data: Panorama }>();
  useEffect(() => {
    if (query.data && !query.isPlaceholderData) {
      previousPanorama.current = {
        scope: panoramaScope,
        data: {
          contadores: query.data.contadores,
          contadores_captura: query.data.contadores_captura,
          contadores_sentido: query.data.contadores_sentido,
          conferencia_ok: query.data.conferencia_ok,
        },
      };
    }
  }, [query.data, query.isPlaceholderData, panoramaScope]);
  // Mantém apenas o panorama dos chips durante a espera, nunca os cartões.
  const panorama =
    query.data ??
    (previousPanorama.current?.scope === panoramaScope ? previousPanorama.current.data : undefined);

  useEffect(() => {
    if (query.isFetching || !pages) return undefined;
    const currentParams: IEmpresasParams = JSON.parse(paramsKey);
    const controller = new AbortController();

    // O bloco da página aberta já veio na própria consulta. Só há trabalho
    // aqui quando o período passa de um bloco (mais de 1000 empresas): os
    // blocos seguintes são buscados em sequência, um por vez.
    // A página aberta continua staleTime=0 e consulta novamente o banco.
    const preload = async () => {
      const { block, pagesPerBlock } = blocoDaPagina(currentParams.page, currentParams.size);
      const lastBlock = Math.ceil(pages / pagesPerBlock);
      for (let next = block + 1; next <= lastBlock; next += 1) {
        if (controller.signal.aborted) break;
        const firstPage = (next - 1) * pagesPerBlock + 1;
        const lastPage = Math.min(pages, firstPage + pagesPerBlock - 1);
        let needsPage = false;
        for (let page = firstPage; page <= lastPage; page += 1) {
          const key = empresasKey({ ...currentParams, page });
          if (
            !queryClient.getQueryData(key) &&
            queryClient.getQueryState(key)?.fetchStatus !== 'fetching'
          )
            needsPage = true;
        }
        if (needsPage) {
          try {
            // Uma navegação ou troca de filtros cancela esta sequência.
            // eslint-disable-next-line no-await-in-loop
            await fetchBlock(queryClient, currentParams, next, undefined, controller.signal);
          } catch {
            // Falha no trabalho opcional não afeta a página nem gera retries.
            break;
          }
        }
      }
    };
    preload();
    return () => controller.abort();
  }, [paramsKey, pages, query.isFetching, queryClient]);

  return { ...query, panorama };
};

// ---------- /empresas/{id}/notas ----------
export interface INotaConferencia {
  tipo: TipoDoc;
  numero: string | null;
  serie: string | null;
  modelo: number | null;
  data_emissao: string | null;
  valor: number | null;
  contraparte_nome: string | null;
  contraparte_cnpj: string | null;
  lado: LadoNota;
  confianca: Confianca | null;
  // `cancelada` = cancelada em QUALQUER fonte (o ✕ da lista). Os dois abaixo dizem
  // ONDE — null = a nota não existe daquele lado. Antes só existia a situação do
  // Domínio, então nota cancelada na fonte aparecia como "autorizada".
  cancelada: boolean;
  cancelada_dominio: boolean | null;
  cancelada_fonte: boolean | null;
  situacao_divergente: boolean;
  devolucao: boolean;
  // saída = devolução própria (você devolveu a compra); entrada = devolução de venda
  // (cliente devolveu sua venda). null quando não é devolução ou é serviço.
  devolucao_tipo: 'propria' | 'venda' | null;
  chave: string | null;
  // Qual fonte externa casou com ESTA nota (NFS-e tem 2: Autmais/Portal — a
  // união das duas é comparada contra o Domínio, cada nota mantém sua origem
  // real). Ausente/`undefined` pra NF-e/NFC-e/CT-e (sempre Autmais).
  fonte?: 'autmais' | 'portal';
  // Só pra NFS-e de entrada "só na fonte externa": true quando a nota já está
  // no Domínio (note-list bruto, extract_notas_entrada) mas a re-extração de
  // impostos ainda não processou ela — não conta como match, é só um aviso
  // (ver ADR-004: sem confirmação de tipo, não promove a "par").
  pendente_reextracao?: boolean;
}
// Contagens por lado (KPIs clicáveis do drawer).
export interface INotasKpi {
  par: number;
  so_dominio: number;
  so_fiscal: number;
  fora_escopo: number;
  dominio: number; // par + so_dominio + fora_escopo
  fiscal: number; // par + so_fiscal
  total: number;
}
// Contagem por situação, sobre o mesmo escopo — NÃO reflete o filtro de situação
// selecionado (senão clicar em "Canceladas" zeraria os outros).
export interface INotasKpiSituacao {
  canceladas: number;
  divergentes: number;
  ativas: number;
}
export type SituacaoFiltro = 'cancelada' | 'ativa' | 'divergente';
export interface INotasDrill {
  itens: INotaConferencia[];
  total: number;
  total_dominio: number;
  total_fiscal_sem_par: number;
  kpi: INotasKpi;
  kpi_situacao: INotasKpiSituacao;
  // fonte da comparação: 'portal' (NFS-e), 'autmais' (mercadoria) ou 'misto'
  // (aba Entradas com os dois). Define o rótulo do chip da outra fonte no drawer.
  fonte_fiscal?: 'portal' | 'autmais' | 'misto';
}

export type BuscaCampo = 'todos' | 'numero' | 'contraparte' | 'cnpj' | 'valor' | 'chave';
type INotasParams = IRange & {
  tipo: string;
  lado?: LadoFiltro;
  tipo_doc?: string;
  busca?: string;
  busca_campo?: BuscaCampo;
  situacao?: SituacaoFiltro;
  limit?: number;
  offset?: number;
};

const getNotas = async (
  empresaId: number,
  params: INotasParams,
  signal?: AbortSignal,
): Promise<INotasDrill> => {
  const res = await api.get<IApiEnvelope<INotasDrill>>(
    `/api/v1/conferencia-fiscal/empresas/${empresaId}/notas`,
    { params, signal },
  );
  return res.data.data;
};

export const useConferenciaNotas = (empresaId: number | null, params: INotasParams) =>
  useQuery({
    queryKey: ['conferencia-fiscal', 'notas', empresaId, params] as const,
    queryFn: ({ signal }) => getNotas(empresaId as number, params, signal),
    enabled: !!empresaId,
    staleTime: 0,
    refetchInterval: 5 * 60 * 1000,
    placeholderData: keepPreviousData,
  });

// ---------- /empresas/{id}/autmais-status ----------
export interface IAutmaisLogItem {
  tipo_consulta: string;
  tipo_nota: string | null;
  modelo_nf: string | null;
  status: string; // success | warning | error | processing | to_process | waiting_in_queue
  qtd_notas: number; // o que o Autmais diz que capturou
  extraido: number; // o que foi realmente extraído nas xml_*
  divergencia: boolean; // capturou com sucesso mas o extraído não bate
  mensagem: string | null;
  mensagem_erro: string | null;
  data_processamento: string | null;
  data_inicio: string | null;
  data_fim: string | null;
  url_zip: string | null;
  url_print: string | null;
}
export interface IAutmaisLog {
  status: AutmaisStatus;
  ultima_captura: string | null;
  resumo: { total: number; ok: number; erro: number; aviso: number; processando: number };
  itens: IAutmaisLogItem[];
}

const getAutmaisStatus = async (
  empresaId: number,
  params: IRange,
  signal?: AbortSignal,
): Promise<IAutmaisLog> => {
  const res = await api.get<IApiEnvelope<IAutmaisLog>>(
    `/api/v1/conferencia-fiscal/empresas/${empresaId}/autmais-status`,
    { params, signal },
  );
  return res.data.data;
};

export const useConferenciaAutmaisStatus = (empresaId: number | null, params: IRange) =>
  useQuery({
    queryKey: ['conferencia-fiscal', 'autmais-status', empresaId, params] as const,
    queryFn: ({ signal }) => getAutmaisStatus(empresaId as number, params, signal),
    enabled: !!empresaId,
    staleTime: 0,
    refetchInterval: 5 * 60 * 1000,
  });

// ---------- notas duplicadas (consumido da tela de duplicadas) ----------
export interface IDuplicadasResumo {
  codi_emp: number;
  quantidade_duplicadas: number;
  quantidade_entrada: number;
  quantidade_saida: number;
}

interface IDuplicadasEnvelope {
  data: IDuplicadasResumo[];
  message?: string;
  meta?: unknown;
}

const getDuplicadas = async (params: IRange): Promise<IDuplicadasResumo[]> => {
  const res = await api.get<IDuplicadasEnvelope>('/api/v1/notas-duplicadas/empresas/', {
    params: { date_start: params.data_ini, date_end: params.data_fim },
  });
  return res.data.data;
};

export const useConferenciaDuplicadas = (params: IRange) =>
  useQuery({
    queryKey: ['conferencia-fiscal', 'duplicadas', params] as const,
    queryFn: () => getDuplicadas(params),
    staleTime: 5 * 60 * 1000,
    refetchInterval: 5 * 60 * 1000,
  });
